/*
 -------------------------------
 DO NOT EDIT THIS FILE
 -------------------------------
 */

import contextFactory from 'yoda-app-shell/lib/contextFactory';
import RestClient from 'yoda-interfaces/lib/RestClient/RestClient';
import { preloadMfeComponents } from 'yoda-app-shell/lib/mfe/getMfeComponent';
import App from '../src/App';
import rootReducer from '../src/reducers';
import rootSaga from '../src/sagas';
import rootActionTypes from '../src/actionTypes';
import rootMiddleware from '../src/middlewares';
import * as micrositeConfig from '../src/config';
import localSettings from './settings';

/**
 * Resolves server-side MFE remote URLs from the runtime moduleFederation context.
 *
 * @param {Object} [settings] - Microsite settings from bootstrap
 * @returns {Object|null} Resolved remote config (name → URL) or null if unavailable
 */
const resolveServerRemotes = (settings) => {
    if (!settings) return null;

    const remotes = settings?.moduleFederation?.common?.remotes?.server
        || settings?.moduleFederation?.remotes?.server
        || null;

    if (!remotes) {
        console.warn('[MFE] No server remotes found in runtime settings');
        return null;
    }

    const resolved = {};
    Object.entries(remotes).forEach(([key, value]) => {
        resolved[key] = typeof value === 'string'
            ? value.split('@').pop() || value
            : value;
    });

    return Object.keys(resolved).length > 0 ? resolved : null;
};

const history = {};
const reducer = rootReducer;
const micrositeParams = {
    ...micrositeConfig,
    localSettings,
};

/**
 * Preloads MFE components for SSR.
 * Remote URLs are resolved from runtime settings, falling back to build-time defaults.
 *
 * @param {Object} [settings] - Microsite settings with moduleFederation config
 */
const preloadMfe = async (settings) => {
    let federatedComponents;
    let setRuntimeRemotes;
    try {
        const federatedConfig = require('../config/federatedComponents');
        federatedComponents = federatedConfig.federatedComponents;
        setRuntimeRemotes = federatedConfig.setRuntimeRemotes;
    } catch (e) {
        federatedComponents = null;
        setRuntimeRemotes = null;
    }

    if (!federatedComponents) return;

    // Resolve remotes from runtime settings first, fall back to build-time config
    const runtimeRemotes = resolveServerRemotes(settings);
    if (runtimeRemotes && setRuntimeRemotes) {
        setRuntimeRemotes(runtimeRemotes);
    }

    const componentPaths = Object.keys(federatedComponents).reduce((acc, key) => {
        if (!key.startsWith('__') && federatedComponents[key].isSSR !== false) {
            acc.push(federatedComponents[key].path);
        }
        return acc;
    }, []);

    const remotes = federatedComponents.__remotes;

    if (componentPaths.length > 0 && remotes) {
        await preloadMfeComponents(componentPaths, remotes);
    }
};

const context = contextFactory(
    App,
    reducer,
    rootSaga,
    rootActionTypes,
    history,
    RestClient,
    micrositeParams,
    rootMiddleware
);

context.preloadMfeComponents = preloadMfe;

export default context;
